import React, {
    createContext,
    useContext,
    useState,
    useRef,
    useEffect,
    useCallback,
} from 'react';

import { cn } from '../../utils/cn';

interface ExpandableDropdownContextType {
    open: boolean;
    setOpen: (open: boolean) => void;
    contentRef: React.MutableRefObject<HTMLDivElement | null>;
    triggerRef: React.MutableRefObject<HTMLButtonElement | null>;
    rootRef: React.MutableRefObject<HTMLDivElement | null>;
    triggerRect: DOMRect | null;
    updateTriggerRect: () => void;
}

const ExpandableDropdownContext = createContext<
    ExpandableDropdownContextType | undefined
>(undefined);

export const useExpandableDropdown = () => {
    const context = useContext(ExpandableDropdownContext);
    if (!context) {
        throw new Error(
            'useExpandableDropdown must be used within an ExpandableDropdown',
        );
    }
    return context;
};

interface ExpandableDropdownProps {
    children: React.ReactNode;
    defaultOpen?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    className?: string;
    disabled?: boolean;
}

export const ExpandableDropdown = ({
    children,
    defaultOpen = false,
    open: controlledOpen,
    onOpenChange,
    className,
    disabled = false,
}: ExpandableDropdownProps) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : uncontrolledOpen;

    const contentRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);

    const updateTriggerRect = useCallback(() => {
        if (triggerRef.current) {
            setTriggerRect(triggerRef.current.getBoundingClientRect());
        }
    }, []);

    const setOpen = useCallback(
        (newOpen: boolean) => {
            if (disabled) return;
            if (newOpen) {
                updateTriggerRect();
            }
            if (!isControlled) {
                setUncontrolledOpen(newOpen);
            }
            onOpenChange?.(newOpen);
        },
        [isControlled, onOpenChange, updateTriggerRect, disabled],
    );

    useEffect(() => {
        if (!open) return;

        // Update rect on scroll or resize to keep alignment if needed
        window.addEventListener('resize', updateTriggerRect);
        window.addEventListener('scroll', updateTriggerRect, true);

        const handleClickOutside = (event: MouseEvent) => {
            if (
                contentRef.current &&
                !contentRef.current.contains(event.target as Node) &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target as Node)
            ) {
                setOpen(false);
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);

        return () => {
            window.removeEventListener('resize', updateTriggerRect);
            window.removeEventListener('scroll', updateTriggerRect, true);
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [open, setOpen, updateTriggerRect]);

    return (
        <ExpandableDropdownContext.Provider
            value={{
                open,
                setOpen,
                contentRef,
                triggerRef,
                rootRef,
                triggerRect,
                updateTriggerRect,
            }}
        >
            <div
                ref={rootRef}
                className={cn('relative inline-block w-full', className)}
            >
                {children}
            </div>
        </ExpandableDropdownContext.Provider>
    );
};
